import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import {
  ServiceObjectReference,
  ServiceObjectReferencesBase,
  updateServiceObjectAddress,
  updateServiceObjectContact,
} from "../types/service-object-references-base";
import t from "@/lang/lang";
import { ComponentProps, useState } from "react";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { ServiceObjectName } from "../../customers/components/service-object-name";
import { TextareaField } from "@/shared/components/field/textarea-field";
import { TextInputField } from "@/shared/components/field/input-field";
import { Separator } from "@/shared/components/ui/separator";

/**
 * ServiceObjectReferencesBaseCard can be used to patch
 * the values saved with {@link ServiceObjectReferencesBase}.
 * It is not intended to manage the references to service
 * objects itself (add / remove). Use {@link ServiceObjectsSelect}
 * for this.
 *
 * Use `hideSelect` if the service kind allows only one service object.
 */
export function ServiceObjectReferencesBaseCard({
  references,
  onReferencesChange,
  hideSelect = false,
  disabled = false,
  ...props
}: ComponentProps<typeof Card> & {
  references: ServiceObjectReferencesBase;
  onReferencesChange: (r: ServiceObjectReferencesBase) => void;
  disabled?: boolean;
  hideSelect?: boolean;
}) {
  const [chosenId, setChosenId] = useState(
    references.serviceObjects[0]?.serviceObjectId,
  );
  // Falls back to the first, so a removed service object is never shown.
  const selected =
    references.serviceObjects.find((s) => s.serviceObjectId === chosenId) ??
    references.serviceObjects[0];
  const selectedId = selected?.serviceObjectId;

  const address = selected?.serviceObjectAddress;
  const contact = selected?.serviceContact;

  const addressFieldNameBase = `serviceObjects.${selectedId}.serviceObjectAddress.`;
  const contactFieldNameBase = `serviceObjects.${selectedId}.serviceContactAddress.`;

  return (
    <Card {...props}>
      <CardHeader>
        <CardTitle>{t("Service Adresse & Kontakt")}</CardTitle>
        <CardDescription>
          {t("Adresse & Kontakt für das gewählte Serviceobjekt.")}
        </CardDescription>
        <CardAction>
          {!hideSelect && references.serviceObjects.length > 1 && (
            <ReferenceSelect
              value={selectedId}
              onValueChange={setChosenId}
              options={references.serviceObjects}
            />
          )}
        </CardAction>
      </CardHeader>
      <CardContent>
        {!selected && (
          <p className="text-muted-foreground text-sm">
            {t("Kein Serviceobjekt ausgewählt.")}
          </p>
        )}
        {address && contact && (
          <div className="grid grid-cols-12 gap-4">
            <TextareaField
              label={t("Kommentar (z.B. Zugänglichkeit)")}
              value={address.comment}
              onChange={(e) =>
                onReferencesChange(
                  updateServiceObjectAddress(references, selectedId, {
                    ...address,
                    comment: e.target.value,
                  }),
                )
              }
              fieldName={addressFieldNameBase + "comment"}
              fieldProps={{ className: "col-span-full" }}
              disabled={disabled}
            />
            <TextInputField
              label={t("Firma")}
              text={address.company}
              onTextChange={(company) =>
                onReferencesChange(
                  updateServiceObjectAddress(references, selectedId, {
                    ...address,
                    company,
                  }),
                )
              }
              fieldName={addressFieldNameBase + "company"}
              fieldProps={{ className: "col-span-full" }}
              disabled={disabled}
            />
            <TextInputField
              label={t("Straße")}
              text={address.street}
              onTextChange={(street) =>
                onReferencesChange(
                  updateServiceObjectAddress(references, selectedId, {
                    ...address,
                    street,
                  }),
                )
              }
              fieldName={addressFieldNameBase + "street"}
              fieldProps={{ className: "col-span-full lg:col-span-6" }}
              disabled={disabled}
            />
            <TextInputField
              label={t("PLZ")}
              text={address.postalCode}
              onTextChange={(postalCode) =>
                onReferencesChange(
                  updateServiceObjectAddress(references, selectedId, {
                    ...address,
                    postalCode,
                  }),
                )
              }
              fieldName={addressFieldNameBase + "postalCode"}
              fieldProps={{ className: "col-span-4 lg:col-span-2" }}
              disabled={disabled}
            />
            <TextInputField
              label={t("Stadt")}
              text={address.city}
              onTextChange={(city) =>
                onReferencesChange(
                  updateServiceObjectAddress(references, selectedId, {
                    ...address,
                    city,
                  }),
                )
              }
              fieldName={addressFieldNameBase + "city"}
              fieldProps={{ className: "col-span-8 lg:col-span-4" }}
              disabled={disabled}
            />
            <Separator className="col-span-full" />
            <TextInputField
              label={t("Ansprechpartner")}
              text={contact.name}
              onTextChange={(name) =>
                onReferencesChange(
                  updateServiceObjectContact(references, selectedId, {
                    ...contact,
                    name,
                  }),
                )
              }
              fieldName={contactFieldNameBase + "name"}
              fieldProps={{ className: "col-span-full" }}
              disabled={disabled}
            />
            <TextInputField
              label={t("Mobil")}
              text={contact.mobilephone}
              onTextChange={(mobilephone) =>
                onReferencesChange(
                  updateServiceObjectContact(references, selectedId, {
                    ...contact,
                    mobilephone,
                  }),
                )
              }
              fieldName={contactFieldNameBase + "mobilephone"}
              fieldProps={{ className: "col-span-full lg:col-span-4" }}
              disabled={disabled}
            />
            <TextInputField
              label={t("Telefon")}
              text={contact.phone}
              onTextChange={(phone) =>
                onReferencesChange(
                  updateServiceObjectContact(references, selectedId, {
                    ...contact,
                    phone,
                  }),
                )
              }
              fieldName={contactFieldNameBase + "phone"}
              fieldProps={{ className: "col-span-full lg:col-span-4" }}
              disabled={disabled}
            />
            <TextInputField
              label={t("E-Mail")}
              text={contact.email}
              onTextChange={(email) =>
                onReferencesChange(
                  updateServiceObjectContact(references, selectedId, {
                    ...contact,
                    email,
                  }),
                )
              }
              fieldName={contactFieldNameBase + "email"}
              fieldProps={{ className: "col-span-full lg:col-span-4" }}
              disabled={disabled}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function ReferenceSelect({
  options,
  ...props
}: ComponentProps<typeof Select> & {
  options: ServiceObjectReference[];
}) {
  return (
    <Select {...props}>
      <SelectTrigger>
        <SelectValue placeholder={t("Bitte Object wählen")} />
      </SelectTrigger>
      <SelectContent>
        <SelectGroup>
          {options.map((ref) => (
            <SelectItem value={ref.serviceObjectId} key={ref.serviceObjectId}>
              <ServiceObjectName id={ref.serviceObjectId} />
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}
